/**
 *  @class
 *  @function CartCutoffTimer
 *
 *  Counts down to a daily cutoff time (e.g. 14:00) in the SHOP's timezone and
 *  hides itself outside its window: after the cutoff it stays hidden until
 *  midnight, and it can be hidden on weekends entirely.
 */
if (!customElements.get('cart-cutoff-timer')) {
  // Never sleep longer than this, so a laptop waking from suspend or a system
  // clock change is picked up within a few minutes instead of at midnight.
  const MAX_SLEEP = 15 * 60 * 1000;

  class CartCutoffTimer extends HTMLElement {
    connectedCallback() {
      this.timerEl = this.querySelector('.cart-cutoff__timer');

      this.hour = parseInt(this.dataset.hour, 10) || 0;
      this.minute = parseInt(this.dataset.minute, 10) || 0;
      this.hideWeekends = this.dataset.hideWeekends === 'true';
      this.alwaysShow = this.dataset.alwaysShow === 'true';
      this.offsetMs = CartCutoffTimer.parseOffset(this.dataset.utcOffset);
      this.units = {
        hours: this.dataset.unitHours || 'h',
        minutes: this.dataset.unitMinutes || 'm',
        seconds: this.dataset.unitSeconds || 's'
      };

      this.onVisibilityChange = () => {
        if (!document.hidden) this.tick();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.tick();
    }

    disconnectedCallback() {
      clearTimeout(this.timeout);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    /**
     * "Now" shifted into the shop's timezone, so the getUTC* accessors read
     * back the shop's wall clock. Differences between two such values are
     * still real elapsed time, because both carry the same shift.
     */
    shopNow() {
      return new Date(Date.now() + this.offsetMs);
    }

    /** Today's cutoff, or a later day's when dayOffset is > 0. */
    deadlineOn(now, dayOffset) {
      return Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + dayOffset,
        this.hour,
        this.minute,
        0,
        0
      );
    }

    /** The next cutoff still ahead of us, skipping weekends when they are off. */
    nextDeadline(now) {
      for (let offset = 0; offset < 8; offset += 1) {
        const deadline = this.deadlineOn(now, offset);
        if (deadline <= now.getTime()) continue;
        const day = new Date(deadline).getUTCDay();
        if (this.hideWeekends && (day === 0 || day === 6)) continue;
        return deadline;
      }
      return null;
    }

    tick() {
      clearTimeout(this.timeout);

      const now = this.shopNow();

      // Override: stay on screen outside the window, counting to the next
      // cutoff rather than to one that has already passed.
      if (this.alwaysShow) {
        const upcoming = this.nextDeadline(now);
        if (upcoming !== null) {
          const ahead = upcoming - now.getTime();
          this.render(ahead);
          this.hidden = false;
          this.schedule(ahead % 1000 || 1000);
          return;
        }
      }

      const day = now.getUTCDay();
      const isWeekend = day === 0 || day === 6;
      const remaining = this.deadlineOn(now, 0) - now.getTime();

      if ((this.hideWeekends && isWeekend) || remaining <= 0) {
        this.hidden = true;
        // Outside the window: wake at the next midnight, when it may reopen.
        const midnight = Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate() + 1,
          0,
          0,
          0,
          0
        );
        this.schedule(midnight - now.getTime() + 1000);
        return;
      }

      this.render(remaining);
      this.hidden = false;
      this.schedule(remaining % 1000 || 1000);
    }

    schedule(delay) {
      this.timeout = setTimeout(() => this.tick(), Math.min(Math.max(delay, 250), MAX_SLEEP));
    }

    render(remaining) {
      if (!this.timerEl) return;

      const total = Math.floor(remaining / 1000);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;

      // Seconds always run; larger units drop off once they are empty, so the
      // line never opens with a dead "0t".
      const parts = [];
      if (hours > 0) parts.push(hours + this.units.hours);
      if (hours > 0 || minutes > 0) {
        parts.push((hours > 0 ? CartCutoffTimer.pad(minutes) : minutes) + this.units.minutes);
      }
      parts.push((parts.length ? CartCutoffTimer.pad(seconds) : seconds) + this.units.seconds);
      const next = parts.join(' ');

      if (this.timerEl.textContent !== next) this.timerEl.textContent = next;
    }

    /** "+0200" / "-0530" -> milliseconds */
    static parseOffset(value) {
      const match = /^([+-])(\d{2})(\d{2})$/.exec((value || '').trim());
      if (!match) return 0;
      const sign = match[1] === '-' ? -1 : 1;
      return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10)) * 60000;
    }

    static pad(value) {
      return value < 10 ? '0' + value : String(value);
    }
  }

  customElements.define('cart-cutoff-timer', CartCutoffTimer);
}
